/* eslint-disable react/display-name */
import React, { useState, useEffect, useMemo } from 'react'
import { withTranslation } from 'react-i18next'
import { gql, useMutation, useQuery } from '@apollo/client'
import VendorComponent from '../components/Vendor/Vendor'
import { getVendors, deleteVendor } from '../apollo'
import {
  Box, Container, Typography, Paper, InputBase,
  IconButton, Menu, MenuItem, ListItemIcon, Button, Dialog,
  DialogTitle, DialogContent, Avatar, Chip, CircularProgress
} from '@mui/material'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import SearchIcon from '@mui/icons-material/Search'
import BusinessIcon from '@mui/icons-material/Business'
import StorefrontIcon from '@mui/icons-material/Storefront'
import PeopleIcon from '@mui/icons-material/People'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import CloseIcon from '@mui/icons-material/Close'

const GET_VENDORS = gql`${getVendors}`
const DELETE_VENDOR = gql`${deleteVendor}`

const stringToColor = s => {
  if (!s) return '#94a3b8'
  let h = 0
  for (let i = 0; i < s.length; i++) h = s.charCodeAt(i) + ((h << 5) - h)
  const p = ['#3b82f6', '#8b5cf6', '#ec4899', '#f97316', '#22c55e', '#14b8a6', '#6366f1']
  return p[Math.abs(h) % p.length]
}

const getInitials = name => {
  if (!name) return '?'
  const parts = name.trim().split(' ')
  return parts.length >= 2 ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase() : name[0].toUpperCase()
}

const ActionMenu = ({ row, onEdit, onDelete }) => {
  const [anchor, setAnchor] = useState(null)
  return (
    <>
      <IconButton size="small" onClick={e => { e.stopPropagation(); setAnchor(e.currentTarget) }} sx={{ color: '#94a3b8', '&:hover': { bgcolor: '#f1f5f9' } }}>
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}
        PaperProps={{ elevation: 0, sx: { borderRadius: '12px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', border: '1px solid #f1f5f9', minWidth: 140 } }}>
        <MenuItem onClick={() => { setAnchor(null); onEdit(row) }} sx={{ gap: 1.5, fontSize: '0.875rem' }}>
          <ListItemIcon sx={{ minWidth: 'auto' }}><EditIcon fontSize="small" sx={{ color: '#3b82f6' }} /></ListItemIcon>
          <Typography fontSize="0.875rem" color="#3b82f6">Edit</Typography>
        </MenuItem>
        <MenuItem onClick={() => { setAnchor(null); onDelete(row) }} sx={{ gap: 1.5, fontSize: '0.875rem' }}>
          <ListItemIcon sx={{ minWidth: 'auto' }}><DeleteIcon fontSize="small" sx={{ color: '#ef4444' }} /></ListItemIcon>
          <Typography fontSize="0.875rem" color="#ef4444">Delete</Typography>
        </MenuItem>
      </Menu>
    </>
  )
}

const Vendors = props => {
  const [editModal, setEditModal] = useState(false)
  const [addModal, setAddModal] = useState(false)
  const [selectedVendor, setSelectedVendor] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')

  const handleViewRestaurants = v => {
    localStorage.setItem('vendorFilterId', v._id)
    localStorage.setItem('activeVendorId', v._id)
    localStorage.setItem('vendorFilterName', v.email || '')
    props.history.push('/restaurant/list')
  }

  useEffect(() => { localStorage.removeItem('restaurant_id'); localStorage.removeItem('activeVendorId') }, [])

  const { loading, error, data, refetch } = useQuery(GET_VENDORS, { fetchPolicy: 'network-only' })
  const [mutateDelete] = useMutation(DELETE_VENDOR, {
    refetchQueries: [{ query: GET_VENDORS }],
    onError: err => console.error('Delete vendor error:', err.message)
  })

  const allVendors = data?.vendors ?? []
  const verified = allVendors.filter(v => v.isEmailVerified).length
  const pending = allVendors.filter(v => !v.isEmailVerified).length

  const filtered = useMemo(() => {
    if (searchQuery.length < 2) return allVendors
    const q = searchQuery.toLowerCase()
    return allVendors.filter(v =>
      (v.name || '').toLowerCase().includes(q) ||
      (v.email || '').toLowerCase().includes(q)
    )
  }, [allVendors, searchQuery])

  return (
    <>
      {/* Hero banner */}
      <Box sx={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 60%, #1e40af 100%)', px: { xs: 2, md: 4 }, pt: 3.5, pb: 5, position: 'relative', overflow: 'hidden' }}>
        <Box sx={{ maxWidth: 1200, mx: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ width: 52, height: 52, borderRadius: '14px', background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <BusinessIcon sx={{ color: '#93c5fd', fontSize: 26 }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: '1.6rem', color: '#fff', lineHeight: 1.15 }}>Merchants</Typography>
              <Typography sx={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.55)', mt: 0.3 }}>Manage your merchant accounts and restaurant owners</Typography>
            </Box>
          </Box>
          <Button
            startIcon={<AddIcon />}
            onClick={() => { setSelectedVendor(null); setAddModal(true) }}
            sx={{ bgcolor: '#f97316', color: '#fff', fontWeight: 700, borderRadius: '12px', px: 2.5, py: 1.2, fontSize: '0.875rem', textTransform: 'none', '&:hover': { bgcolor: '#ea6c0a' }, boxShadow: '0 4px 12px rgba(249,115,22,0.4)' }}>
            Add Merchant
          </Button>
        </Box>
      </Box>

      <Container maxWidth="xl" sx={{ mt: 3, pb: 4 }}>
        {/* Stat cards */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2, mb: 3 }}>
          {[
            { label: 'Total Merchants', value: allVendors.length, icon: <PeopleIcon sx={{ color: '#3b82f6', fontSize: 26 }} />, bg: '#eff6ff', border: '#bfdbfe' },
            { label: 'Verified', value: verified, icon: <CheckCircleIcon sx={{ color: '#16a34a', fontSize: 26 }} />, bg: '#f0fdf4', border: '#bbf7d0' },
            { label: 'Pending Verification', value: pending, icon: <HourglassEmptyIcon sx={{ color: '#d97706', fontSize: 26 }} />, bg: '#fffbeb', border: '#fde68a' },
          ].map((card, i) => (
            <Paper key={i} elevation={0} sx={{ borderRadius: '16px', border: `1px solid ${card.border}`, p: 2.5, display: 'flex', alignItems: 'center', gap: 2, background: card.bg, boxShadow: '0 2px 12px rgba(0,0,0,0.05)' }}>
              <Box sx={{ width: 52, height: 52, borderRadius: '14px', bgcolor: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                {card.icon}
              </Box>
              <Box>
                <Typography sx={{ fontSize: '2rem', fontWeight: 800, color: '#0f172a', lineHeight: 1 }}>
                  {loading ? <CircularProgress size={20} /> : card.value}
                </Typography>
                <Typography sx={{ fontSize: '0.8rem', color: '#64748b', mt: 0.3 }}>{card.label}</Typography>
              </Box>
            </Paper>
          ))}
        </Box>

        {/* Table */}
        <Paper elevation={0} sx={{ borderRadius: '20px', border: '1px solid #e2e8f0', boxShadow: '0 4px 24px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, justifyContent: 'space-between', gap: { xs: 1.5, sm: 0 }, px: 3, py: 2.5, borderBottom: '1px solid #f1f5f9' }}>
            <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#1e293b' }}>
              All Merchants{' '}
              <Typography component="span" sx={{ fontSize: '0.85rem', color: '#94a3b8', fontWeight: 400 }}>({filtered.length})</Typography>
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', px: 1.5, py: 0.8, width: { xs: '100%', sm: 260 } }}>
              <SearchIcon sx={{ color: '#94a3b8', fontSize: 18 }} />
              <InputBase placeholder="Search by name or email..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} sx={{ fontSize: '0.875rem', flex: 1, color: '#475569' }} />
            </Box>
          </Box>

          {error && <Box sx={{ px: 3, py: 2, bgcolor: '#fef2f2' }}><Typography sx={{ fontSize: '0.83rem', color: '#dc2626' }}>Error: {error.message}</Typography></Box>}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              {/* Table header */}
              <Box sx={{ minWidth: 560, display: 'grid', gridTemplateColumns: '2.5fr 1.5fr 1.5fr 60px', px: 3, py: 1.5, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                {['Merchant', 'Status', 'Restaurants', ''].map((h, i) => (
                  <Typography key={i} sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{h}</Typography>
                ))}
              </Box>

              {filtered.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <BusinessIcon sx={{ fontSize: 48, color: '#e2e8f0', mb: 1 }} />
                  <Typography sx={{ color: '#94a3b8' }}>No merchants found</Typography>
                </Box>
              ) : filtered.map((v, i) => (
                <Box key={v._id} sx={{ minWidth: 560, display: 'grid', gridTemplateColumns: '2.5fr 1.5fr 1.5fr 60px', px: 3, py: 1.8, alignItems: 'center', borderBottom: i < filtered.length - 1 ? '1px solid #f1f5f9' : 'none', '&:hover': { bgcolor: '#fafafa' } }}>
                  {/* Merchant */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar sx={{ width: 38, height: 38, bgcolor: stringToColor(v.name || v.email), fontSize: '0.85rem', fontWeight: 700, flexShrink: 0 }}>
                      {getInitials(v.name || v.email)}
                    </Avatar>
                    <Box>
                      <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#1e293b' }}>{v.name || '—'}</Typography>
                      <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8' }}>{v.email}</Typography>
                    </Box>
                  </Box>

                  {/* Status */}
                  {v.isEmailVerified ? (
                    <Chip
                      icon={<CheckCircleIcon sx={{ fontSize: '14px !important', color: '#16a34a !important' }} />}
                      label="Verified"
                      size="small"
                      sx={{ bgcolor: '#dcfce7', color: '#16a34a', fontWeight: 600, fontSize: '0.75rem', height: 24, width: 'fit-content', '& .MuiChip-icon': { ml: '6px' } }}
                    />
                  ) : (
                    <Chip
                      icon={<HourglassEmptyIcon sx={{ fontSize: '14px !important', color: '#d97706 !important' }} />}
                      label="Pending"
                      size="small"
                      sx={{ bgcolor: '#fef3c7', color: '#d97706', fontWeight: 600, fontSize: '0.75rem', height: 24, width: 'fit-content', '& .MuiChip-icon': { ml: '6px' } }}
                    />
                  )}

                  {/* Restaurants */}
                  <Box
                    onClick={() => handleViewRestaurants(v)}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', borderRadius: '8px', px: 1, py: 0.5, '&:hover': { bgcolor: '#eff6ff' } }}
                  >
                    <Box sx={{ width: 28, height: 28, borderRadius: '8px', bgcolor: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <StorefrontIcon sx={{ fontSize: 14, color: '#3b82f6' }} />
                    </Box>
                    <Typography sx={{ fontSize: '0.875rem', color: '#3b82f6', fontWeight: 600, textDecoration: 'underline', textDecorationColor: 'transparent', '&:hover': { textDecorationColor: '#3b82f6' } }}>
                      {v.restaurants?.length ?? 0} Restaurants
                    </Typography>
                  </Box>

                  {/* Actions */}
                  <ActionMenu
                    row={v}
                    onEdit={row => { setSelectedVendor(row); setEditModal(true) }}
                    onDelete={row => mutateDelete({ variables: { id: row._id } })}
                  />
                </Box>
              ))}
            </Box>
          )}
        </Paper>
      </Container>

      {/* Add Modal */}
      <Dialog open={addModal} onClose={() => setAddModal(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '20px' } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 2.5, px: 3, pb: 1 }}>
          <Typography fontWeight={700} fontSize="1rem">Add New Merchant</Typography>
          <IconButton size="small" onClick={() => setAddModal(false)}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pb: 3 }}>
          <VendorComponent onClose={() => { setAddModal(false); refetch() }} />
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={editModal} onClose={() => setEditModal(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '20px' } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 2.5, px: 3, pb: 1 }}>
          <Typography fontWeight={700} fontSize="1rem">Edit Merchant</Typography>
          <IconButton size="small" onClick={() => setEditModal(false)}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pb: 3 }}>
          <VendorComponent vendor={selectedVendor} onClose={() => { setEditModal(false); refetch() }} />
        </DialogContent>
      </Dialog>
    </>
  )
}

export default withTranslation()(Vendors)